import { isFunction } from 'lodash';
import { hubScenesCreate, hubScenesEdit } from '../../api/localMeshBot';

/**
 * Saves a MeshBot (scene/rule) by calling the appropriate hub API — edit or create.
 * Selects `hubScenesEdit` when `meshBotId` is truthy and `isSystemRole` is false;
 * otherwise falls back to `hubScenesCreate`.
 * If the API call fails and an `onError` callback is provided, the error is passed to
 * the callback and the function returns silently; otherwise the error is rethrown.
 *
 * @async
 * @param {string|null} meshBotId - The existing MeshBot ID to edit, or null/undefined when creating.
 * @param {string} serial - Hub serial number (e.g., "90004631").
 * @param {Object} rule - Full scene/rule configuration object to persist.
 * @param {boolean} isSystemRole - When true the function always calls create, ignoring `meshBotId`.
 * @param {Function} [onError] - Optional error callback. When provided it is called with the
 *   caught error and the function returns without rethrowing.
 *
 * @returns {Promise<void>} Resolves when the API call succeeds; returns void on handled errors.
 *
 * @throws {Error} Rethrows the API error when `onError` is not a function.
 *
 * @example
 * // Create a new MeshBot
 * await meshBotSaveApi(null, '90004631', ruleConfig, false, (err) => console.error(err));
 *
 * @example
 * // Edit an existing MeshBot
 * await meshBotSaveApi('scene-abc123', '90004631', ruleConfig, false, (err) => console.error(err));
 */
export const meshBotSaveApi = async (meshBotId, serial, rule, isSystemRole, onError) => {
    try {
        const api = meshBotId && !isSystemRole ? hubScenesEdit : hubScenesCreate;

        await api(serial, rule);
    } catch (e) {
        if (isFunction(onError)) {
            onError(e);

            return;
        }

        throw e;
    }
};
